import { useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { cn } from "../../lib/utils";

// Searchable, grouped multi-select. Selected values show as tokens in the field;
// Backspace on an empty input removes the last one.
//
// `groups` are quick picks shown first. `searchGroups` are longer lists
// (e.g. regions, countries, every city) shown under them and narrowed as you
// type. Only the first rows of each render, for speed.

const EMPTY_QUERY_LIMIT = 100;
const SEARCH_LIMIT = 60;

export type SearchGroup = {
  label: string;
  options: string[];
  /** Rows shown before typing (default 100). */
  emptyLimit?: number;
};

type Section = { label: string; options: string[]; total: number };

// Lowercase and strip accents so "montreal" matches "Montréal".
const normalize = (text: string) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

type MultiSelectProps = {
  groups: Record<string, string[]>;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  id?: string;
  searchGroups?: SearchGroup[];
  /** Offer "Add “…”" for text that isn't in any list. */
  allowCustom?: boolean;
};

export default function MultiSelect({
  groups,
  value,
  onChange,
  placeholder,
  id,
  searchGroups = [],
  allowCustom = false,
}: MultiSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Open upward when the field is low on screen (e.g. Locations)
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery] = useState("");

  const toggle = (option: string) =>
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);

  const q = normalize(query.trim());
  const grouped = useMemo(() => new Set(Object.values(groups).flat()), [groups]);
  // Normalised once per list; quick picks are left out so nothing shows twice
  const searchIndexes = useMemo(
    () =>
      searchGroups.map((g) => ({
        ...g,
        index: g.options.filter((o) => !grouped.has(o)).map((o) => [o, normalize(o)] as const),
      })),
    [searchGroups, grouped]
  );

  const quickSections: Section[] = Object.entries(groups).map(([label, options]) => {
    const matches = options.filter((o) => normalize(o).includes(q));
    return { label, options: matches, total: matches.length };
  });

  // Long lists: names starting with the query first, then any other match.
  const searchSections: Section[] = searchIndexes.map(({ label, index, emptyLimit = EMPTY_QUERY_LIMIT }) => {
    if (!q) return { label, options: index.slice(0, emptyLimit).map(([o]) => o), total: index.length };
    const starts: string[] = [];
    const contains: string[] = [];
    for (const [o, n] of index) {
      if (n.startsWith(q)) starts.push(o);
      else if (n.includes(q)) contains.push(o);
    }
    const all = starts.concat(contains);
    return { label, options: all.slice(0, SEARCH_LIMIT), total: all.length };
  });

  const sections = quickSections.concat(searchSections).filter((sec) => sec.options.length > 0);

  const typed = query.trim();
  const exactMatch =
    Array.from(grouped).some((o) => normalize(o) === q) ||
    // "montreal" matches "Montréal, QC": compare the name before the comma too
    searchIndexes.some((g) => g.index.some(([, n]) => n === q || n.split(",")[0] === q)) ||
    value.some((v) => normalize(v) === q);
  const customOption = allowCustom && typed && !exactMatch ? typed : null;

  return (
    <div className="relative">
      <div
        ref={boxRef}
        onClick={() => inputRef.current?.focus()}
        className={cn(
          "flex min-h-[44px] cursor-text flex-wrap items-center gap-1.5 rounded-[10px] border border-melt-hair bg-white py-1.5 pl-2 pr-9 transition-shadow",
          open && "border-melt-caramel shadow-[0_0_0_3px_rgba(217,136,41,.15)]"
        )}
      >
        {value.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-[7px] bg-melt-peach py-1 pl-[9px] pr-1.5 text-[12.5px] font-medium text-melt-text1"
          >
            {v}
            <button
              type="button"
              aria-label={`Remove ${v}`}
              onClick={(e) => {
                e.stopPropagation();
                toggle(v);
              }}
              className="grid h-4 w-4 place-items-center rounded text-melt-text2 hover:bg-melt-text1/10"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          value={query}
          placeholder={value.length ? "" : placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            const rect = boxRef.current?.getBoundingClientRect();
            if (rect) {
              const below = window.innerHeight - rect.bottom;
              setDropUp(below < 300 && rect.top > below);
            }
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
            setQuery("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !query && value.length) onChange(value.slice(0, -1));
            if (e.key === "Escape") inputRef.current?.blur();
            if (e.key === "Enter") {
              e.preventDefault();
              const first = sections[0]?.options[0] ?? customOption;
              if (first) {
                toggle(first);
                setQuery("");
              }
            }
          }}
          className="min-w-[120px] flex-1 bg-transparent px-1.5 py-1 text-sm text-melt-text1 outline-none placeholder:text-melt-text3"
        />
        <ChevronDown size={14} className="pointer-events-none absolute right-3.5 top-[15px] text-melt-text3" />
      </div>

      {open && (
        <div
          className={cn(
            "absolute inset-x-0 z-20 max-h-[280px]",
            dropUp ? "bottom-[calc(100%+6px)]" : "top-[calc(100%+6px)]"
          )}
        >
        <div className="scrollbar-thin max-h-[280px] overflow-auto rounded-xl border border-melt-hair bg-melt-menu p-1.5 shadow-[0_22px_40px_-20px_rgba(92,52,23,.5)]">
          {sections.length === 0 && !customOption ? (
            <div className="px-2.5 py-3 text-[13px] text-melt-text3">No matches</div>
          ) : (
            sections.map(({ label, options, total }) => (
              <div key={label || "top"}>
                {label && (
                  <div className="px-2.5 pb-1.5 pt-2.5 font-plex text-[10.5px] font-medium uppercase leading-none tracking-[.1em] text-melt-text3">
                    {label}
                  </div>
                )}
                {options.map((option) => {
                  const selected = value.includes(option);
                  return (
                    <div
                      key={option}
                      role="option"
                      aria-selected={selected}
                      // mousedown + preventDefault keeps focus in the input so the list stays open
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggle(option);
                      }}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-melt-text1 hover:bg-melt-text1/5"
                    >
                      <span
                        className={cn(
                          "grid h-[17px] w-[17px] flex-none place-items-center rounded-[5px] border-[1.5px] border-melt-text1/[.28]",
                          selected && "border-melt-caramel bg-melt-caramel"
                        )}
                      >
                        <Check size={12} className={cn("text-white", !selected && "opacity-0")} />
                      </span>
                      {option}
                    </div>
                  );
                })}
                {total > options.length && (
                  <div className="px-2.5 py-2 text-xs text-melt-text3">
                    {q ? "Keep typing to narrow down" : `Type to search all ${total.toLocaleString()}`}
                  </div>
                )}
              </div>
            ))
          )}
          {customOption && (
            <div
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                toggle(customOption);
                setQuery("");
              }}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-melt-text1 hover:bg-melt-text1/5"
            >
              <Plus size={15} className="text-melt-text3" />
              Add “{customOption}”
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}
